import { CONVEYANCE_TYPES, type Location, type WebConfig } from "@koala/sdk";
import loadGoogleMapsApi from "load-google-maps-api";
import isEqual from "lodash/isEqual";
import { Component } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { scroller } from "react-scroll";
import { compose } from "redux";
import { withTheme } from "styled-components";
import { StyledMap, StyledMapOuter, StyledMapSearchButton } from "../styles";
import { mapTooltip } from "../tooltip";
import { settings, styles } from "./map-config";
import { GOOGLE_LIBRARIES } from "@/constants/global";
import { LAYOUT } from "@/constants/styles";
import globalActions from "@/redux/global/actions";
import { type RootState } from "@/types/app";
import * as ErrorReporter from "@/utils/errorReporter";
import { fireGaEvent, gaActions, gaCats } from "@/utils/googleAnalytics";

interface ThemeProps {
  theme: WebConfig;
}

interface Props extends ReduxProps, ThemeProps {
  locations: Location[];
  activeLocationId?: number;
  setActiveLocation: (id: number) => void;
  locationSearchTimestamp: string;
  fetchCenterCoords: (coords: number[], distance: number) => void;
  showMapButton: () => void;
  hideMapButton: boolean;
}

class Map extends Component<Props> {
  map: any = null;
  markers: google.maps.Marker[] = [];
  activeInfoWindow: any = null;

  state = {
    displayMapButton: false,
    isMobile: false,
  };

  async componentDidMount() {
    try {
      const googleMaps = await loadGoogleMapsApi({
        key: this.props.theme.locations.map_key,
        libraries: [GOOGLE_LIBRARIES.PLACES], // always load Google Places Library to support autocomplete
      });

      this.map = new googleMaps.Map(document.getElementById("map")!, {
        ...settings,
        styles,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
      });
      this.drawLocations(this.props.locations, this.props.activeLocationId);
    } catch (error) {
      ErrorReporter.captureException(error);
      console.error(error);
    }

    // Determine whether mobile mode and set on state
    this.setState({
      isMobile: window.matchMedia(`(max-width: 767px)`).matches,
    });
  }

  componentDidUpdate(prevProps: Props) {
    // Ensure the map has initialized before attempting to modify it
    if (this.map) {
      // If locations change, reset the map
      if (!isEqual(prevProps.locations, this.props.locations)) {
        this.clearMarkers(() => {
          this.drawLocations(this.props.locations, this.props.activeLocationId);
        });
      } else if (prevProps.activeLocationId !== this.props.activeLocationId) {
        // If only the active index has changed, update the markers
        this.updateMapMarkers(
          this.props.locations,
          /** @TODO ensure that activeLocationId is defined. */
          // @ts-expect-error
          this.props.activeLocationId
        );
      }
    }
  }

  getActiveLocation = (locations: Location[], activeLocationId: number) =>
    locations.find((location) => location.id === activeLocationId);

  clearMarkers = (cb: any) => {
    if (typeof google === "undefined") {
      return;
    }

    // Ensure the map is centered now that we'd resized div#map
    google.maps.event.trigger(this.map, "resize");

    // Clear all previous markers
    this.markers.forEach((marker: any) => {
      marker.setMap(null);
    });

    this.markers = [];

    // Add new locations to the map
    cb();
  };

  deriveWindowInfoContent = (location: Location) => {
    if (typeof google === "undefined") {
      return;
    }

    const { strings } = this.props;
    const contentString: string =
      strings &&
      mapTooltip(
        location,
        () =>
          this.props.toggleFulfillmentModal(
            true,
            location,
            CONVEYANCE_TYPES.DELIVERY
          ),
        this.props.theme,
        strings,
        "t0.",
        this.props.basketFulfillment,
        this.props.theme
      );

    return new google.maps.InfoWindow({
      content: contentString,
    });
  };

  updateMapMarkers = (locations: Location[], activeLocationId: number) => {
    if (this.activeInfoWindow !== null) {
      this.activeInfoWindow.close();
    }

    const activeLocation = this.getActiveLocation(locations, activeLocationId);

    this.markers.forEach((marker: google.maps.Marker) => {
      const markerPosition = marker.getPosition();
      const isActive = Boolean(
        markerPosition?.lat() === activeLocation?.cached_data?.latitude &&
          markerPosition?.lng() === activeLocation?.cached_data?.longitude &&
          /** @TODO ensure that getTitle() doesn't return null. */
          // @ts-expect-error
          marker.getTitle().includes(activeLocation?.label)
      );

      const icon = this.getMarker(isActive);

      if (isActive && !this.state.isMobile && activeLocation) {
        this.activeInfoWindow = this.deriveWindowInfoContent(activeLocation);
        this.activeInfoWindow.open(this.map, marker);
      }

      marker.setIcon(icon);
    });
  };

  fitBounds() {
    if (typeof google === "undefined") {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    this.markers.forEach((marker) => {
      const position = marker.getPosition();

      if (!position) {
        return;
      }

      if (marker.getVisible()) {
        bounds.extend(position);
      }
    });

    this.map.fitBounds(bounds);
  }

  getMarker = (isActive = false) => {
    const { theme } = this.props;

    if (theme.locations.map_icon && theme.locations.map_icon_active) {
      return isActive
        ? theme.locations.map_icon_active
        : theme.locations.map_icon;
    }

    return {
      path: "M0.399902 21.0595C0.399902 9.64069 9.57363 0.399994 20.7643 0.399994C31.9549 0.399994 41.1287 9.64069 40.9985 21.0595C40.9985 31.8737 23.0819 51.3109 21.2763 53.2697C21.2194 53.3314 21.1785 53.3758 21.1547 53.402C21.0245 53.534 20.8944 53.6 20.6992 53.6C20.504 53.6 20.3739 53.534 20.2438 53.402C20.2199 53.3758 20.1791 53.3314 20.1222 53.2697C18.3165 51.3109 0.399902 31.8737 0.399902 21.0595ZM21 29C25.9706 29 30 24.9706 30 20C30 15.0294 25.9706 11 21 11C16.0294 11 12 15.0294 12 20C12 24.9706 16.0294 29 21 29Z",
      fillColor: theme.global.primary_active_color,
      fillOpacity: isActive ? 1 : 0.5,
      strokeWeight: 0,
      rotation: 0,
      scale: 0.7,
      anchor: new google.maps.Point(20, 53),
    };
  };

  drawLocations = (locations: Location[], activeLocationId?: number) => {
    if (typeof google === "undefined") {
      return;
    }

    if (locations?.length) {
      locations.map((location: Location) => {
        const marker = new google.maps.Marker({
          icon: this.getMarker(location.id === activeLocationId),
          map: this.map,
          position: {
            // @ts-expect-error
            lat: location?.cached_data?.latitude,
            // @ts-expect-error
            lng: location?.cached_data?.longitude,
          },
          title: `Click to expand details ${location.label}`,
        });

        if (
          activeLocationId &&
          location.id === activeLocationId &&
          !this.state.isMobile
        ) {
          // Don't open Google Maps InfoWindow popups on mobile
          const activeLocation = this.getActiveLocation(
            locations,
            activeLocationId
          );

          if (!activeLocation) {
            return;
          }

          this.activeInfoWindow = this.deriveWindowInfoContent(activeLocation);
          this.activeInfoWindow.open(this.map, marker);
        }

        // Add click listener to marker so we can interact with them
        google.maps.event.addListener(marker, "click", () => {
          this.props.setActiveLocation(location.id);
          scroller.scrollTo(`Location-${location.id}`, {
            duration: 500,
            smooth: true,
            offset: -LAYOUT.HEADERHEIGHT,
          });
        });

        // Push the marker into the array
        this.markers.push(marker);
      });

      // Fit the map area to accommodate all markers
      this.fitBounds();
    }

    this.displayMapButton();
  };

  getMapCenter = () => {
    const mapCenterCoords = [];

    // Fetch coordinates
    const w: number = this.map.getBounds().getSouthWest().lng();
    const s: number = this.map.getBounds().getSouthWest().lat();

    const centerLng: number = this.map.getCenter().lng();
    const centerLat: number = this.map.getCenter().lat();

    // Set center coordinates
    mapCenterCoords[0] = centerLat;
    mapCenterCoords[1] = centerLng;

    // Approximate degrees to miles
    const horizontalDistance: number = (centerLng - w) * 69.2;
    const verticalDistance: number = (centerLat - s) * 69.2;

    // Pick the larger distance - center to top or center to left
    const largestRadialDistance: number = Math.max(
      horizontalDistance,
      verticalDistance
    );

    this.props.fetchCenterCoords(mapCenterCoords, largestRadialDistance);
    this.setState({ displayMapButton: false });

    // Fire Events
    fireGaEvent(gaCats.findShop, gaActions.mapSearchArea);
  };

  displayMapButton = () => {
    this.map.addListener("dragend", () => {
      this.setState({ displayMapButton: true });
      this.props.showMapButton();
    });
  };

  render() {
    return (
      <StyledMapOuter>
        {this.state.displayMapButton && !this.props.hideMapButton && (
          <StyledMapSearchButton
            onClick={this.getMapCenter}
            buttonTheme="secondary"
          >
            Search This Area
          </StyledMapSearchButton>
        )}
        <StyledMap id="map" />
      </StyledMapOuter>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  basketFulfillment: state.app.basket.fulfillment,
  strings: state.app.cmsConfig.strings,
});

const mapDispatchToProps = {
  toggleFulfillmentModal: globalActions.toggleFulfillmentModal,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default compose(withTheme, connector)(Map);
